import { ArrowRight, FileLock2, ShieldCheck, UserCheck, Server, AlertTriangle } from 'lucide-react';
import type { PublicRoutePath } from './publicRoutes';

type SecurityPageProps = {
  onNavigate?: (path: PublicRoutePath, options?: { hash?: string | null }) => void;
};

type SectionCardProps = {
  id: string;
  eyebrow: string;
  title: string;
  icon: JSX.Element;
  paragraphs: string[];
  points: string[];
};

function SectionCard({ id, eyebrow, title, icon, paragraphs, points }: SectionCardProps) {
  return (
    <section id={id} className="reveal workstation-frame rounded-[2.5rem] border border-zinc-200 bg-white p-8 text-[#0F172A] md:p-10">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <div className="mb-3 text-[11px] font-bold uppercase tracking-[0.35em] text-pink-500">{eyebrow}</div>
          <h2 className="text-3xl font-bold tracking-tight text-zinc-900 md:text-4xl">{title}</h2>
        </div>
        <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl border border-zinc-200 bg-zinc-50 text-pink-500 shadow-sm">
          {icon}
        </div>
      </div>

      <div className="space-y-4">
        {paragraphs.map((paragraph) => (
          <p key={paragraph} className="max-w-3xl text-sm font-normal leading-7 text-zinc-500 md:text-[15px]">
            {paragraph}
          </p>
        ))}
      </div>

      <div className="mt-8 grid gap-4">
        {points.map((point) => (
          <div key={point} className="rounded-[1.5rem] border border-zinc-200 bg-zinc-50 px-5 py-4 text-sm font-normal leading-7 text-zinc-600">
            {point}
          </div>
        ))}
      </div>
    </section>
  );
}

const OVERVIEW_POINTS = [
  'Security is considered throughout product development and product operations.',
  'Access to sensitive systems is limited to the people who need it to keep the service running.',
  'Platform design aims to reduce unnecessary exposure of account and trade data.',
  'Security practices continue to improve as the platform and its operating needs evolve.',
];

const DATA_POINTS = [
  'Account data is used to authenticate users, manage access, and support account-level product workflows.',
  'Imported and uploaded trade data is used to power journaling, replay, analytics, and support workflows.',
  'Connected broker or integration data is handled only to support the features enabled by your account and configuration.',
  'Trade-Proof does not claim more data handling than it needs for the product functions it provides.',
];

const ACCOUNT_POINTS = [
  'Account access depends on the authentication methods available in the application at launch.',
  'Users are responsible for protecting their credentials and keeping login details confidential.',
  'Strong passwords, careful session hygiene, and device security all help protect account access.',
  'If you notice unexpected account activity, report it promptly through the public contact flow.',
];

const INFRASTRUCTURE_POINTS = [
  'Production systems are accessed only through controlled channels and limited to necessary operational use.',
  'Internal access is intended to stay narrow rather than broadly distributed.',
  'Infrastructure and service choices are made with reliability and security in mind.',
  'General logging and monitoring support operational awareness without turning this page into a technical promise list.',
];

export default function SecurityPage({ onNavigate }: SecurityPageProps) {
  return (
    <div className="mx-auto max-w-6xl px-8 pb-16 pt-48 text-left text-[#0F172A]">
      <section className="reveal mb-10 grid gap-8 lg:grid-cols-[1.08fr_0.92fr] lg:items-start">
        <div>
          <div className="mb-5 text-[11px] font-bold uppercase tracking-[0.45em] text-pink-500">Public Security Hub</div>
          <h1 className="mb-6 text-5xl font-bold tracking-tight text-zinc-900 md:text-5xl">Security</h1>
          <p className="max-w-5xl text-lg font-normal leading-relaxed text-zinc-500">
            Trade-Proof is built with a strong focus on protecting account access, trade data, and platform integrity.
            This page explains our current security posture at a high level so users can understand how the product
            approaches protection and how to report a concern.
          </p>

          <div className="mt-8 flex flex-wrap gap-3">
            <a href="#security-overview" className="rounded-full border border-zinc-200 bg-white px-5 py-2 text-[11px] font-bold uppercase tracking-[0.24em] text-zinc-500 transition-colors hover:border-pink-300 hover:text-pink-600">Overview</a>
            <a href="#data-protection" className="rounded-full border border-zinc-200 bg-white px-5 py-2 text-[11px] font-bold uppercase tracking-[0.24em] text-zinc-500 transition-colors hover:border-pink-300 hover:text-pink-600">Data</a>
            <a href="#report-security-issue" className="rounded-full border border-zinc-200 bg-white px-5 py-2 text-[11px] font-bold uppercase tracking-[0.24em] text-zinc-500 transition-colors hover:border-pink-300 hover:text-pink-600">Report</a>
          </div>
        </div>

        <div className="reveal workstation-frame rounded-[2.5rem] border border-zinc-200 bg-zinc-50 p-8 md:p-10">
          <div className="mb-4 text-[11px] font-bold uppercase tracking-[0.35em] text-zinc-400">Launch Summary</div>
          <div className="space-y-4">
            <div className="rounded-[1.5rem] border border-zinc-200 bg-white px-5 py-4">
              <div className="text-[10px] font-bold uppercase tracking-[0.24em] text-zinc-400">Scope</div>
              <div className="mt-2 text-base font-bold text-zinc-900">Account, data, access, and reporting guidance</div>
            </div>
            <div className="rounded-[1.5rem] border border-zinc-200 bg-white px-5 py-4">
              <div className="text-[10px] font-bold uppercase tracking-[0.24em] text-zinc-400">Reporting path</div>
              <div className="mt-2 text-base font-bold text-zinc-900">Public contact form with Security Issue selected</div>
            </div>
            <div className="rounded-[1.5rem] border border-zinc-200 bg-white px-5 py-4">
              <div className="text-[10px] font-bold uppercase tracking-[0.24em] text-zinc-400">Important</div>
              <div className="mt-2 text-base font-bold text-zinc-900">No unsupported compliance or audit claims are made here.</div>
            </div>
          </div>
        </div>
      </section>

      <section className="reveal mb-10 rounded-[2.5rem] border border-sky-200 bg-[linear-gradient(135deg,rgba(240,249,255,1),rgba(255,255,255,0.96))] p-8 text-[#0F172A] shadow-[0_18px_45px_rgba(15,23,42,0.06)] md:p-10">
        <div className="mb-4 flex items-center gap-3 text-sky-700">
          <ShieldCheck size={20} strokeWidth={2.4} />
          <span className="text-[11px] font-bold uppercase tracking-[0.35em]">Security posture</span>
        </div>
        <h2 className="max-w-4xl text-3xl font-bold tracking-tight text-zinc-900 md:text-4xl">
          Security is part of how the product is built, operated, and reviewed.
        </h2>
        <p className="mt-5 max-w-4xl text-base font-normal leading-8 text-zinc-600">
          The goal is not to make grand claims. It is to keep access limited, handle data carefully, and reduce
          unnecessary exposure where Trade-Proof stores or processes user information.
        </p>
      </section>

      <div className="grid gap-8">
        <SectionCard
          id="security-overview"
          eyebrow="Overview"
          title="Security overview"
          icon={<ShieldCheck size={20} strokeWidth={2.2} />}
          paragraphs={[
            'Security is considered throughout product development and operational decision-making.',
            'Trade-Proof aims to keep the product small in scope and careful about what it exposes, both to users and to internal operators.',
          ]}
          points={OVERVIEW_POINTS}
        />

        <SectionCard
          id="data-protection"
          eyebrow="Data Protection"
          title="How trade data is handled"
          icon={<FileLock2 size={20} strokeWidth={2.2} />}
          paragraphs={[
            'Account data, imported trade data, and connected broker or integration data are used only to operate the features that rely on them.',
            'Trade-Proof does not try to dress this up as a compliance statement. It is a practical description of how the product uses information it receives.',
          ]}
          points={DATA_POINTS}
        />

        <SectionCard
          id="account-security"
          eyebrow="Accounts"
          title="Account security"
          icon={<UserCheck size={20} strokeWidth={2.2} />}
          paragraphs={[
            'Account protection starts with keeping login access controlled and using the authentication tools available in the application.',
            'Users should treat their credentials as sensitive, avoid sharing them, and keep their devices and browser sessions in good hygiene.',
          ]}
          points={ACCOUNT_POINTS}
        />

        <SectionCard
          id="infrastructure-access-controls"
          eyebrow="Operations"
          title="Infrastructure and access controls"
          icon={<Server size={20} strokeWidth={2.2} />}
          paragraphs={[
            'Production access is limited to what is necessary to keep the platform running and support the service.',
            'The product uses general logging and monitoring to support operational awareness, but this page avoids pretending that those practices amount to formal assurance claims.',
          ]}
          points={INFRASTRUCTURE_POINTS}
        />

        <section id="report-security-issue" className="reveal workstation-frame rounded-[2.5rem] border border-zinc-200 bg-zinc-100 p-8 text-[#0F172A] md:p-10">
          <div className="mb-6 flex items-start justify-between gap-4">
            <div>
              <div className="mb-3 text-[11px] font-bold uppercase tracking-[0.35em] text-pink-500">Report</div>
              <h2 className="text-3xl font-bold tracking-tight text-zinc-900 md:text-4xl">Report a security issue</h2>
            </div>
            <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl border border-zinc-200 bg-white text-pink-500 shadow-sm">
              <AlertTriangle size={20} strokeWidth={2.2} />
            </div>
          </div>

          <p className="max-w-3xl text-sm font-normal leading-7 text-zinc-600 md:text-[15px]">
            Use the public contact form to report security concerns. Choose <span className="font-semibold text-zinc-900">Security Issue</span> as the inquiry type so the message routes to the right inbox.
          </p>

          <div className="mt-8 grid gap-5 lg:grid-cols-[1fr_auto] lg:items-center">
            <div className="rounded-[1.5rem] border border-zinc-200 bg-white px-5 py-4 text-sm font-normal leading-7 text-zinc-600">
              Include as much practical detail as you can: what you saw, when you saw it, and any steps needed to reproduce the issue.
            </div>
            <button
              type="button"
              onClick={() => onNavigate?.('/help', { hash: 'contact' })}
              className="inline-flex items-center justify-center gap-3 whitespace-nowrap rounded-full bg-[#0F172A] px-7 py-3 text-[11px] font-bold uppercase tracking-[0.24em] text-white shadow-xl transition-all active:scale-95 hover:bg-black"
            >
              Open Contact Form <ArrowRight size={14} className="transition-transform hover:translate-x-1" />
            </button>
          </div>
        </section>

        <section className="reveal workstation-frame rounded-[2.5rem] border border-zinc-200 bg-white p-8 text-[#0F172A] md:p-10">
          <div className="mb-4 text-[11px] font-bold uppercase tracking-[0.35em] text-pink-500">Trust</div>
          <h2 className="text-3xl font-bold tracking-tight text-zinc-900 md:text-4xl">Security is ongoing work.</h2>
          <p className="mt-5 max-w-4xl text-base font-normal leading-8 text-zinc-600">
            Safeguards will continue to evolve as Trade-Proof grows, integrations expand, and operational needs change.
            The platform should be treated as something that gets safer through careful maintenance, not something that
            is ever finished.
          </p>
        </section>
      </div>
    </div>
  );
}